<template>
    <div>

        <div class="kgr-row">
            <kgr-note-box
                title-key="followUpCosts.noteBox.title"
                text-key="followUpCosts.noteBox.text"
                html
            />
        </div>
        <kgr-form-scaffold
            :with-back-button="false"
            :pending="pending"
            :with-primary-buttons="followUpCostConfigs.length > 0"
            @primaryBtnClicked="save"
        >
            <kgr-empty-data
                v-if="followUpCostConfigs.length === 0 && !loading"
                :value="$t('Es sind noch keine Folgebeiträge-Sets vorhanden')"
            />
            <kgr-loading v-if="loading" />
            <kgr-table
                v-else-if="followUpCostConfigs.length > 0"
                style="width: 100%"
                :is-data-table="false"
            >
                <thead>
                    <tr class="kgr-table__row">
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Gebührenkategorie') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Abrechnungsbeginn') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Folgebeiträge') }}
                        </th>
                        <th class="kgr-table__cell kgr-table__cell--headline">
                            {{ $t('Aktionen') }}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr
                        v-for="(config, index) in followUpCostConfigs"
                        :key="index"
                        class="kgr-table__row"
                        :class="{'kgr-table__row--deleted': config.deleted}"
                    >
                        <td class="kgr-table__cell vertical-align-top">
                            {{ $t('cost.category.' + config.category) }}
                        </td>
                        <td class="kgr-table__cell vertical-align-top">
                            <kgr-dropdown
                                :options="invoicingStartOptions"
                                :value="invoicingStartOptions.find(i => i.value === config.invoicingStart)"
                                no-null-option
                                display="label"
                                @post-input="onInvoicingStart(config, $event)"
                            />
                        </td>
                        <td class="kgr-table__cell">
                            <div
                                v-if="config.followUpCosts.length === 0"
                                style="cursor: pointer"
                                @click="openAddCostDialog(config)"
                            >
                                <kgr-empty-data
                                    :value="$t('followUpCostConfigurations.noFollowUpCosts')"
                                />
                            </div>

                            <div
                                v-else
                                class="kgr-grid kgr-grid--tiny-full kgr-grid--small-full kgr-grid--medium-1of3 kgr-grid--large-1of4 kgr-grid--xlarge-fit kgr-grid--gutters"
                            >
                                <div
                                    v-for="(followUpCost,i) in config.followUpCosts"
                                    :key="i"
                                    :class="{'kgr-card--deleted': followUpCost.deleted}"
                                    class="kgr-card kgr-grid__cell kgr-text--noWrap kgr-text kgr-text--truncated"
                                    @click="showFollowUpCostDialog(followUpCost)"
                                >
                                    <p class="kgr-paragraph kgr-text--bold">
                                        {{ $t('Ab {startAge} Jahr(en)', {startAge: followUpCost.startAge}) }}
                                    </p>
                                    <div
                                        :title="$t('followUpCost.hover.edit', {followUpCost: followUpCost.cost.title})"
                                        class=" kgr-text kgr-text--truncated kgr-row kgr-text--noWrap"
                                    >
                                        {{ followUpCost.cost.title }}<br />
                                        {{ getChargeLevelString(followUpCost) }}
                                    </div>
                                </div>
                            </div>

                        </td>
                        <td class="kgr-table__cell vertical-align-top">
                            <kgr-icon-add
                                :title="$t('Folgebeitrag hinzufügen')"
                                @click="openAddCostDialog(config)"
                            />
                            <kgr-icon-reset
                                v-if="config.deleted"
                                @click="reset(config)"
                            />
                            <kgr-icon-delete
                                v-if="!config.deleted"
                                :title="$t('Folgebeitrag löschen')"
                                @click="deleteConfig(config)"
                            />
                        </td>
                    </tr>
                </tbody>
            </kgr-table>
            <br />
            <kgr-button
                icon-add
                @click="addConfig()"
            >
                {{ $t('Folgebeitrags-Set erstellen') }}
            </kgr-button>
        </kgr-form-scaffold>
        <kgr-modal headline="Bitte wählen" :value="showNewConfigDialog" @close="showNewConfigDialog = false">
            <kgr-form-row
                :label="$t('Wählen Sie den Abrechnungsbeginn aus')"
                :error="error"
                field-name="invoicingStart"
            >
                <kgr-dropdown :options="invoicingStartOptions" display="label" v-model="initInvoicingStart"/>
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Wählen Sie einen Gebührenkategorie aus')"
                :error="error"
                field-name="category"
            >
                <kgr-dropdown :options="categories" display="label" v-model="initCategory"/>
            </kgr-form-row>
            <template #footer>
                <kgr-button @click="showNewConfigDialog = false">{{ $t('Abbrechen') }}</kgr-button>
                <kgr-button @click="createConfig" :disabled="initCategory === null || initInvoicingStart === null">{{ $t('Folgebeitrags-Set erstellen') }}</kgr-button>
            </template>
        </kgr-modal>
        <kgr-modal headline="Bitte wählen" v-model="showAddCostDialog" @close="closeFollowUpCostsDialog()" v-if="editFollowUpCostConfig">
            <kgr-form-row
                :label="$t('Wählen Sie eine Gebühr aus')"
                :error="error"
                field-name="addCost"
            >
                <kgr-dropdown :options="getCostsByCategory(editFollowUpCostConfig.category)" display="title" v-model="addCost"/>
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Wählen Sie ein Start-Alter aus')"
                :error="error"
                field-name="addStartAge"
            >
                <kgr-number-input v-model="addStartAge" integer-only/>
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Wählen Sie ein Gebührenstufe aus')"
                :error="error"
                field-name="addChargeLevel"
            >
                <kgr-dropdown display="label" :options="addChargeLevels" v-model="addChargeLevel"/>
            </kgr-form-row>
            <template #footer>
                <kgr-button @click="closeFollowUpCostsDialog()" type="white">{{ $t('Abbrechen') }}</kgr-button>
                <kgr-button @click="addFollowUpCost()" :disabled="addCost === null || addChargeLevel === null || addStartAge === null">{{ $t('Folgebeitrag hinzufügen') }}</kgr-button>
            </template>
        </kgr-modal>
        <kgr-modal :headline="editFollowUpCost.cost.title + ' bearbeiten'" v-model="showEditFollowUpCostDialog" @close="closeFollowUpCostsDialog()" v-if="editFollowUpCost">
            <kgr-form-row
                :label="$t('Wählen Sie eine Gebühr aus')"
                :error="error"
                field-name="editCost"
            >
                <kgr-dropdown v-if="editCost" :options="getCostsByCategory(editFollowUpCost.config.category)" display="title" v-model="editCost" :initial-option="getCostsByCategory(editFollowUpCost.config.category).find(c => c.id === editCost.id)"/>
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Wählen Sie ein Start-Alter aus')"
                :error="error"
                field-name="editStartAge"
            >
                <kgr-number-input v-model="editStartAge" integer-only />
            </kgr-form-row>
            <kgr-form-row
                :label="$t('Wählen Sie ein Gebührenstufe aus')"
                :error="error"
                field-name="editChargeLevel"
            >
                <kgr-dropdown v-if="editCost" display="label" :options="editCost.chargeLevels" v-model="editChargeLevel" />
            </kgr-form-row>
            <template #footer>
                <kgr-button @click="showEditFollowUpCostDialog = false" type="white">{{ $t('Abbrechen') }}</kgr-button>
                <kgr-button @click="deleteFollowUpCost()" type="red">{{ $t('Folgebeitrag löschen') }}</kgr-button>
                <kgr-button @click="updateFollowUpCost()" :disabled="editCost === null || editChargeLevel === null || editStartAge === null">{{ $t('Übernehmen') }}</kgr-button>
            </template>
        </kgr-modal>
    </div>
</template>

<script>
import CostRepository from '../../js/repository/CostRepository';
import Dropdown from '../../components/common/form/select/Dropdown.vue';
import EmptyData from '../../components/common/EmptyData.vue';
import Error from '../../js/model/Error';
import FollowUpCostConfiguration from '../../js/dto/FollowUpCostConfiguration';
import FollowUpCostConfigurationRepository from '../../js/repository/FollowUpCostConfigurationRepository';
import FormRow   from '../../components/common/form/FormRow.vue';
import FormScaffold from '../../components/common/form/FormScaffold.vue';
import GenericModal from '../../components/common/modal/GenericModal.vue';
import IconAdd from '../../components/common/icon/IconAdd.vue';
import IconDelete from '../../components/common/icon/IconDelete.vue';
import IconReset from '../../components/common/icon/IconReset.vue';
import KgrButton from '../../components/common/form/button/KgrButton';
import Loading from '../../components/common/Loading';
import NoteBox from '../../components/common/NoteBox.vue';
import NumberInput from '../../components/common/form/input/NumberInput.vue';
import Table from '../../components/common/table/Table.vue';

export default {
    components: {
        'kgr-button': KgrButton,
        'kgr-loading': Loading,
        'kgr-empty-data': EmptyData,
        'kgr-note-box': NoteBox,
        'kgr-form-scaffold': FormScaffold,
        'kgr-icon-reset': IconReset,
        'kgr-dropdown': Dropdown,
        'kgr-modal': GenericModal,
        'kgr-icon-delete': IconDelete,
        'kgr-table': Table,
        'kgr-icon-add': IconAdd,
        'kgr-form-row': FormRow,
        'kgr-number-input': NumberInput,
    },
    props: {
        kindergartenId: {
            type: Number,
            required: true,
        },
    },
    data() {
        return {
            error: new Error(),
            loading: true,
            pending: false,
            showNewConfigDialog: false,
            showAddCostDialog: false,
            initCategory: null,
            addStartAge: null,
            addCost: null,
            addChargeLevel: null,
            editStartAge: null,
            editCost: null,
            editChargeLevel: null,
            editChargeLevelOptions: [],
            initInvoicingStart: null,
            editFollowUpCostConfig: null,
            editFollowUpCost: null,
            showEditFollowUpCostDialog: false,
            costs: [],
            invoicingStartOptions: [
                { label: this.$t('Taggenau'), value: 'at_day_of_age' },
                { label: this.$t('Anfang des Folgemonats'), value: 'next_month' },
                { label: this.$t('Anfang des Monats'), value: 'current_month' },
            ],
            categories: [
                { label: this.$t('cost.category.care_type'), value: 'care_type'},
                { label: this.$t('cost.category.care_monthly'), value: 'care_monthly'},
                { label: this.$t('cost.category.care_daily'), value: 'care_daily'},
                { label: this.$t('cost.category.care_hourly'), value: 'care_hourly'},
                { label: this.$t('cost.category.food_monthly'), value: 'food_monthly'},
                { label: this.$t('cost.category.food_daily'), value: 'food_daily'},
                { label: this.$t('cost.category.food_single'), value: 'food_single'},
                { label: this.$t('cost.category.other_monthly'), value: 'other_monthly'},
                { label: this.$t('cost.category.other_daily'), value: 'other_daily'},
                { label: this.$t('cost.category.other_single'), value: 'other_single'},
            ],
            followUpCostConfigs: [],
        }
    },
    computed: {
        addChargeLevels() {
            if(this.addCost === null) {
                return [];
            }
            return this.addCost.chargeLevels;
        },
    },
    created() {
        this.loadConfigs();
        this.loadCosts();
    },
    methods: {
        getChargeLevelString(followUpCost) {
            const cost = this.costs.find(cost => cost.id === followUpCost.cost.id);
            return cost ? cost.chargeLevels.find(l => l.value === followUpCost.chargeLevel).label : ''
        },
        showFollowUpCostDialog(followUpCost) {
            this.editCost = followUpCost.cost;
            this.editStartAge = followUpCost.startAge;
            const cost = this.costs.find(cost => cost.id === followUpCost.cost.id);
            this.editCost.category = cost.category;
            this.editChargeLevel = cost.chargeLevels.find(l => l.value === followUpCost.chargeLevel);
            this.editCost.chargeLevels = cost.chargeLevels;
            this.editFollowUpCost = followUpCost;
            this.showEditFollowUpCostDialog = true;
        },
        validateAdd() {
            this.error = new Error();
            //check no followUpCost exists with same startAge OR same cost and chargeLevel (globally
            if(!this.validateRange(this.addStartAge)) {
                this.error.add('addStartAge', this.$t('Dieser Wert sollte zwischen {min} und {max} liegen.', {min: 0, max: 99}));
            }
            if(!Number.isInteger(this.addStartAge)) {
                return false;
            }

            const allConfigs = this.followUpCostConfigs.filter(config => this.editFollowUpCostConfig.category === config.category);
            allConfigs.forEach(config => {
                config.followUpCosts.forEach(followUpCost => {
                    if(followUpCost.deleted) {
                        return;
                    }
                    if(followUpCost.cost.id === this.addCost.id && followUpCost.chargeLevel === this.addChargeLevel.value) {
                        this.error.add('addCost', this.$t('Eine Folgebeitrag mit dieser Kategorie und Gebührenstufe existiert bereits.'));
                    }
                });
            });

            this.editFollowUpCostConfig.followUpCosts.forEach(followUpCost => {
                if(followUpCost.deleted) {
                    return;
                }
                if(followUpCost.startAge === this.addStartAge) {
                    this.error.add('addStartAge', this.$t('Eine Folgebeitrag mit diesem Alter existiert bereits in diesem Set.'));
                }
            });
            return this.error.isEmpty();
        },
        validateEdit() {
            this.error = new Error();

            if(!this.validateRange(this.editStartAge)) {
                this.error.add('editStartAge', this.$t('Dieser Wert sollte zwischen {min} und {max} liegen.', {min: 0, max: 99}));
            }
            if(!Number.isInteger(this.editStartAge)) {
                return false;
            }

            const allConfigs = this.followUpCostConfigs.filter(config => this.editFollowUpCost.config.category === config.category);
            allConfigs.forEach(config => {
                config.followUpCosts.forEach(followUpCost => {
                    if(followUpCost === this.editFollowUpCost || followUpCost.deleted) {
                        return;
                    }
                    if(followUpCost.cost.id === this.editCost.id && followUpCost.chargeLevel === this.editChargeLevel.value) {
                        this.error.add('editCost', this.$t('Eine Folgebeitrag mit dieser Kategorie und Gebührenstufe existiert bereits.'));
                    }
                });
            });

            this.editFollowUpCost.config.followUpCosts.forEach(followUpCost => {
                if(followUpCost.deleted) {
                    return
                }
                if(followUpCost.startAge === this.editStartAge && followUpCost !== this.editFollowUpCost) {
                    this.error.add('editStartAge', this.$t('Eine Folgebeitrag mit diesem Alter existiert bereits in diesem Set.'));
                }
            });
            return this.error.isEmpty();
        },
        validateRange(number) {
            return number >= 0 && number <= 99;
        },
        updateFollowUpCost() {
            if(!this.validateEdit()) {

                return;
            }
            this.showEditFollowUpCostDialog = false;
            this.editFollowUpCost.chargeLevel = this.editChargeLevel.value;
            this.editFollowUpCost.cost = this.editCost;
            this.editFollowUpCost.startAge = this.editStartAge;
            this.editFollowUpCost.config.sortFollowUpCosts();
            this.$nextTick(() => {
                this.editCost = null;
                this.editStartAge = null;
                this.editChargeLevel = null;
            });
        },
        deleteFollowUpCost() {
            this.showEditFollowUpCostDialog = false;
            this.editFollowUpCost.deleted = true;
            this.editCost = null;
            this.editStartAge = null;
            this.editChargeLevel = null;
            this.editFollowUpCost = null;
        },
        onInvoicingStart(config, invoicingStart) {
            config.invoicingStart = invoicingStart.value;
        },
        getCostsByCategory(category) {
            return this.costs.filter(cost => cost.category === category);
        },
        reset(config) {
            config.deleted = false;
        },
        save() {
            this.pending = true;
            FollowUpCostConfigurationRepository.update(this.kindergartenId, this.followUpCostConfigs)
                .then(() => {
                    this.$notify.success('saveChanges.success')
                })
                .then(() => this.loadConfigs())
                .catch(() => {
                    this.$notify.error('saveChanges.error')
                })
                .finally(() => this.pending = false);
        },
        addConfig() {
            this.showNewConfigDialog = true;
        },
        openAddCostDialog(followUpCostConfig) {
            this.editFollowUpCostConfig = followUpCostConfig;
            this.showAddCostDialog = true;
        },
        closeFollowUpCostsDialog() {
            this.showAddCostDialog = false;
            this.addCost = null;
            this.addStartAge = null;
            this.addChargeLevel = null;
            this.$nextTick(() => {
                this.editFollowUpCostConfig = null;
            });
        },
        addFollowUpCost() {
            if(!this.validateAdd()) {
                return;
            }
            this.editFollowUpCostConfig.addFollowUpCost(this.addStartAge, this.addCost, this.addChargeLevel.value)
            this.editFollowUpCostConfig.sortFollowUpCosts();
            this.closeFollowUpCostsDialog();
        },
        createConfig() {
            this.followUpCostConfigs.push(new FollowUpCostConfiguration(this.kindergartenId, this.initInvoicingStart.value, this.initCategory.value));
            this.showNewConfigDialog = false;
            this.initInvoicingStart = null;
            this.initCategory = null;
        },
        deleteConfig(config) {
            if(config.id === null) {
                this.followUpCostConfigs = this.followUpCostConfigs.filter(c => c !== config);
                return;
            }
            config.deleted = true;
        },
        loadConfigs() {
            this.loading = true;
            FollowUpCostConfigurationRepository
                .findByKindergarten(this.kindergartenId)
                .then(data => {
                    this.followUpCostConfigs = data;
                    this.loading = false;
                })
                .catch(() => this.$notify.error('error.loadingData'))
                .finally(() => this.loading = false)
            ;
        },
        loadCosts() {
            CostRepository
                .findAllByKindergarten(this.kindergartenId)
                .then(response => {
                    this.costs = response;
                    this.loading = false;
                })
                .catch(() => this.$notify.error('error.loadingData'));
        },
    },
}
</script>
<style scoped>
.kgr-card {
    display:inline-block;
    padding:0.5rem;
    margin:0.5rem;
    cursor: pointer;
    background-color: var(--kgr-color-gray-light);
}
.kgr-card.kgr-card--deleted {
    background-color: #f8d7da;
    text-decoration: line-through;
}
.kgr-card span {
    text-align: left;
}
.vertical-align-top {
    vertical-align: top;
}
.kgr-table__row.kgr-table__row--deleted {
    text-decoration: line-through;
}
.kgr-table__row.kgr-table__row--deleted td{
    background-color: #f8d7da;
}
</style>
